import { useQuery } from '@tanstack/react-query';
import { entityLookupService, EntityLookupParams } from '../services/entityLookupService';

export const entityLookupKeys = {
  all: ['entityLookup'] as const,
  search: (programId: string, entityType: string, params: EntityLookupParams) =>
    [...entityLookupKeys.all, 'search', programId, entityType, params] as const,
  detail: (programId: string, entityType: string, entityId: string) =>
    [...entityLookupKeys.all, 'detail', programId, entityType, entityId] as const,
  hierarchy: (programId: string, entityType: string, entityId: string) =>
    [...entityLookupKeys.all, 'hierarchy', programId, entityType, entityId] as const,
};

/**
 * Search entities with optional filtering. Supports debounced search text,
 * parent entity filtering (hierarchy cascade), and pagination.
 */
export function useEntityLookup(
  programId: string | undefined,
  entityType: string | undefined,
  params: EntityLookupParams = {},
  enabled = true
) {
  const canQuery = !!programId && !!entityType && enabled;

  return useQuery({
    queryKey: entityLookupKeys.search(programId!, entityType!, params),
    queryFn: () => entityLookupService.searchEntities(programId!, entityType!, params),
    enabled: canQuery,
    select: (res) => res.data,
    staleTime: 30_000, // Cache entity lookups for 30s
  });
}

/**
 * Get a single entity by ID (for display resolution).
 */
export function useEntityById(
  programId: string | undefined,
  entityType: string | undefined,
  entityId: string | undefined
) {
  const canQuery = !!programId && !!entityType && !!entityId;

  return useQuery({
    queryKey: entityLookupKeys.detail(programId!, entityType!, entityId!),
    queryFn: () => entityLookupService.getEntityById(programId!, entityType!, entityId!),
    enabled: canQuery,
    select: (res) => res.data,
    staleTime: 60_000,
  });
}

/**
 * Resolve full hierarchy from an entity (walks up the chain).
 */
export function useEntityHierarchy(
  programId: string | undefined,
  entityType: string | undefined,
  entityId: string | undefined,
  enabled = true
) {
  const canQuery = !!programId && !!entityType && !!entityId && enabled;

  return useQuery({
    queryKey: entityLookupKeys.hierarchy(programId!, entityType!, entityId!),
    queryFn: () => entityLookupService.resolveHierarchy(programId!, entityType!, entityId!),
    enabled: canQuery,
    select: (res) => res.data,
    staleTime: 60_000,
  });
}
